import { z } from "zod";

export const achPaymentAccountSchema = z.object({
	accountHolderName: z.string(),
	accountNumberLast4: z.string(),
	Last4: z.string().optional(),
	accountType: z.string(),
	paymentMethodToken: z.string(),
	paymentMethod: z.literal("ACH"),
	isDefault: z.boolean(),
	bankName: z.string().optional(),
	routingNumber: z.string().optional(),
	address: z.string().optional(),
});

export const ccPaymentAccountSchema = z.object({
	cardholderName: z.string().nullable(),
	cardNumberLast4: z.string(),
	Last4: z.string().optional(),
	cardType: z.string(),
	isExpired: z.boolean(),
	paymentMethodToken: z.string(),
	paymentMethod: z.literal("CreditCard"),
	isDefault: z.boolean(),
	expiration: z.string().optional(),
});

export const paymentAccountSchema = z.discriminatedUnion("paymentMethod", [
	achPaymentAccountSchema,
	ccPaymentAccountSchema,
]);

// GET Request Schemas //

// Endpoint: /api/payments/pay-accounts
export const customerPaymentMethodsSchema = z.object({
	paymentMerchantCustomerId: z.string(),
	paymentAccounts: z.array(paymentAccountSchema),
});

export const customerPaymentMethodsArraySchema = z.array(
	customerPaymentMethodsSchema,
);

// Endpoint: /api/payments/account-balances?customerId=:customerId
export const customerBalanceSchema = z.object({
	pastDue: z.number(),
	dueSoon: z.number(),
	outstanding: z.number(),
});

export const customerPaymentMethodSchema = z.object({
	paymentMethodID: z.number(),
	paymentMethodName: z.string(),
});

export const customerPaymentMethodArraySchema = z.array(
	customerPaymentMethodSchema,
);

// Invoice Schema
export const invoiceSchema = z.object({
	id: z.number().optional(),
	lmCustomerID: z.number(),
	invoiceID: z.string(),
	tqlDocumentNumber: z.string().nullable(),
	poNumber: z.number().nullable(),
	custPO: z.string().nullable(),
	docdate: z.string(),
	invoiceStatusID: z.number(),
	lastDatePaid: z.string().nullable(),
	invoiceAmt: z.number(),
	balanceAmt: z.number(),
	paidAmt: z.number(),
	loadDate: z.string().nullable(),
	deliveryDate: z.string().nullable(),
	age: z.number().nullable(),
	invoiceStatus: z.string().nullable(),
	invoiceType: z.number(),
	dueDate: z.string().nullable(),
	rate: z.number().optional(),
	firstPickupState: z.string().nullable().optional(),
	lastDropState: z.string().nullable().optional(),
	mode: z.string().nullable().optional(),
});

export const invoiceArraySchema = z.array(invoiceSchema);

const summaryObject = z.object({
	count: z.number(),
	sum: z.number(),
});

export const invoiceSummarySchema = z.object({
	pastDueAmount: summaryObject,
	dueSoonAmount: summaryObject,
	outstandingBalance: summaryObject,
});

export const invoiceDocumentSchema = z.object({
	documentID: z.number(),
	documentDescription: z.string(),
	documentFileName: z.string(),
	categoryDocumentID: z.number(),
});

export const invoiceDocumentArraySchema = z.array(invoiceDocumentSchema);

// Type inference
export type CustomerBalance = z.infer<typeof customerBalanceSchema>;
export type customerPaymentMethodSchema = z.infer<
	typeof customerPaymentMethodSchema
>;
export type Invoice = z.infer<typeof invoiceSchema>;
export type InvoiceDocument = z.infer<typeof invoiceDocumentSchema>;

export type InvoiceSummary = z.infer<typeof invoiceSummarySchema>;

export type ACHPaymentAccount = z.infer<typeof achPaymentAccountSchema>;
export type CCPaymentAccount = z.infer<typeof ccPaymentAccountSchema>;
export type PaymentAccount = z.infer<typeof paymentAccountSchema>;
export type CustomerPaymentMethods = z.infer<
	typeof customerPaymentMethodsSchema
>;
